/**
 * Pure functions for evaluating "are we currently in quiet hours?" and "when
 * does the next active-hours window start?" in the contact's timezone.
 *
 * Mirrors `server/services/organization/quiet-hours-service.js`; the server
 * remains authoritative for actual message scheduling. These helpers exist so
 * the in-chat banner and modal can derive UI state without an HTTP roundtrip.
 */

import { QUERY_KEYS } from '@app/constants/service';
import i18n from '@app/i18n';
import { formatRelativeTime } from '@app/i18n/formatters/relative-time';
import type { Job } from '@app/types/api/job';
import type {
  DaySchedule,
  Organization,
  QuietHoursConfig,
} from '@app/types/api/organization';

interface QueryClientLike {
  getQueryData?: (queryKey: readonly unknown[]) => unknown;
}

/** Calendar parts of an instant as read in a given timezone (not a UTC moment). */
interface ZonedDateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  dayOfWeek: number; // 0=Sun..6=Sat (matches Date#getDay)
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Date parts (year/month/day/hour/minute/dayOfWeek) of `instant` as a
 *  calendar in `timeZone` would read them. */
export const getDateParts = (
  instant: Date,
  timeZone: string,
): ZonedDateParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);

  const lookup: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') lookup[p.type] = p.value;
  });
  // Intl can emit "24" for midnight in some locales — normalise to 0.
  const rawHour = Number(lookup.hour);
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(lookup.minute),
    dayOfWeek: DAY_NAME_TO_INDEX[lookup.weekday] ?? 0,
  };
};

/**
 * Convert calendar parts (interpreted in `timeZone`) to UTC ms.
 *
 * Algorithm — single-pass offset measurement, DST-correct:
 *   1. Treat the input parts as if they were UTC. That gives a *candidate*
 *      UTC ms (`naiveUtc`) that's wrong by exactly the zone's offset at the
 *      target moment.
 *   2. Project the candidate back into `timeZone`. The result tells us what
 *      a calendar in that zone would read for the candidate.
 *   3. The delta between the input parts and the projected parts is the
 *      zone's offset (positive west of UTC, negative east).
 *   4. Add the delta back to `naiveUtc` to land on the true UTC ms.
 *
 * This works across DST transitions because step 2 asks `Intl` for the actual
 * offset that applies at that moment — no hard-coded ±5h/±4h table.
 */
export const partsToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number => {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
  // What does a calendar in `timeZone` read for the naive candidate?
  const projectedParts = getDateParts(new Date(naiveUtc), timeZone);
  const projectedPartsAsUtc = Date.UTC(
    projectedParts.year,
    projectedParts.month - 1,
    projectedParts.day,
    projectedParts.hour,
    projectedParts.minute,
  );
  // (naiveUtc - projectedPartsAsUtc) is the zone's offset at this instant.
  return naiveUtc + (naiveUtc - projectedPartsAsUtc);
};

/**
 * Contact IANA timezone from the end-user on the case, matching `User` model
 * resolution (`user.timeZone ?? profile.timeZone`). Null means render the
 * generic fallback copy — org default is intentionally NOT used here.
 */
export const getContactTimeZone = (job: Job | undefined): string | null => {
  const customer = job?.customer;
  if (!customer) return null;
  return customer.timeZone ?? customer.profile?.timeZone ?? null;
};

/**
 * Resolves org quiet-hours config the same way the in-chat banner does:
 * prefer the React Query organization cache (authoritative after fetch), then
 * fall back to Redux `state.organizations.quietHours` for legacy paths.
 */
export const resolveOrganizationQuietHours = ({
  organizationId,
  queryClient,
  reduxQuietHours,
}: {
  organizationId?: string | null;
  queryClient?: QueryClientLike | null;
  reduxQuietHours?: QuietHoursConfig;
}): QuietHoursConfig | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.quietHours !== undefined) {
      return cached.quietHours;
    }
  }
  return reduxQuietHours;
};

const minutesOfDay = (parts: ZonedDateParts): number =>
  parts.hour * 60 + parts.minute;

/**
 * True when `now` falls outside the configured active-hours window. Signature
 * differs from the server's (which takes a job and resolves tz async): on the
 * FE we already have config + tz + clock, so we keep this pure and memoizable
 * for the reselect chain.
 */
export const isCurrentlyQuietHours = (
  quietHours: QuietHoursConfig | undefined,
  timeZone: string | null,
  now: Date,
): boolean => {
  if (!quietHours?.enabled || !timeZone) return false;
  const parts = getDateParts(now, timeZone);
  const day: DaySchedule | undefined = quietHours.schedule?.[parts.dayOfWeek];
  if (!day) return false;
  const mins = minutesOfDay(parts);
  // Minute-granularity only — use >= on end so the final minute of active
  // hours (e.g. 8:00:01–8:00:59) matches the server, which compares full
  // timestamps against endTime with seconds zeroed (`current > endTimeZoned`).
  return !day.enabled || mins < day.startTimeMins || mins >= day.endTimeMins;
};

/** YYYY-MM-DD calendar key for `instant` as observed in `timeZone`. */
export const calendarDayKeyInZone = (
  instant: Date,
  timeZone: string,
): string => {
  const parts = getDateParts(instant, timeZone);
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${parts.year}-${month}-${day}`;
};

/** Whole calendar days from `from` until `to` in `timeZone` (0 = same day). */
export const calendarDaysUntilInZone = (
  from: Date,
  to: Date,
  timeZone: string,
): number => {
  const targetKey = calendarDayKeyInZone(to, timeZone);
  let cursor = from;
  for (let offset = 0; offset < 366; offset += 1) {
    if (calendarDayKeyInZone(cursor, timeZone) === targetKey) {
      return offset;
    }
    const parts = getDateParts(cursor, timeZone);
    cursor = new Date(
      partsToUtcMs(parts.year, parts.month, parts.day + 1, 12, 0, timeZone),
    );
  }
  return 366;
};

/**
 * Human-readable send-day label for quiet-hours-deferred messages. Avoids
 * surfacing odd exact times (e.g. 9:09 AM) — designs call for Today /
 * Tomorrow / weekday / relative day instead.
 */
export const formatQuietHoursSendDay = (
  sendTimeMs: number | string | Date | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string => {
  if (sendTimeMs === null || sendTimeMs === undefined || sendTimeMs === '') {
    return '';
  }
  const sendDate =
    sendTimeMs instanceof Date ? sendTimeMs : new Date(sendTimeMs);
  if (Number.isNaN(sendDate.getTime())) return '';

  const zone =
    timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const daysUntil = calendarDaysUntilInZone(now, sendDate, zone);

  if (daysUntil === 0) {
    return i18n.t('date:today');
  }
  if (daysUntil === 1) {
    return i18n.t('date:tomorrow');
  }
  if (daysUntil >= 2 && daysUntil <= 6) {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        timeZone: zone,
      }).format(sendDate);
    } catch {
      return new Intl.DateTimeFormat('en', {
        weekday: 'long',
        timeZone: zone,
      }).format(sendDate);
    }
  }
  return formatRelativeTime(sendDate, 'long');
};

export const CENTRALIZED_QUIET_HOURS_FEATURE_NAME = 'CENTRALIZED_QUIET_HOURS';

// SSM kill switch only - the per-org override is checked via
// useIsCentralizedQuietHoursEnabled
export const isCentralizedQuietHoursFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_CENTRALIZED_QUIET_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};
